// The quiz's own data shapes — ported 1:1 in copy from after-www's
// BuildAQuote question set, restated as plain data the flow's own screens
// render (no JSX baked into the data, unlike the source).

/** A run of a label's text, with the flow's emphasis style applied to
 * the segments the source marked bold (`<span className="font-bold
 * text-evergreen">…</span>`). */
export interface LabelSegment {
  text: string;
  emphasize?: boolean;
}

export interface QuestionOption {
  key: string;
  label: string;
  /** Educational copy the source carries for a future "explain this
   * match" surface; nothing in this port renders it (there is no plan
   * recommendation here — see the quiz spec). */
  caption?: string;
  /** Illustration path for the goals multiselect. */
  image?: string;
}

export interface ResponseTestimonial {
  quote: string;
  author: string;
  source: string;
  rating: number;
}

export interface ResponseContent {
  title?: string;
  text?: string;
  steps?: string[];
  testimonial?: ResponseTestimonial;
  /** Names the phone number the dead-end screen offers — resolved
   * against `CARE_TEAM_PHONE` for `"hospice"`. */
  phoneKey?: "hospice";
  externalLink?: string;
  externalLinkText?: string;
}

export type QuestionType =
  "singleselect" | "multiselect" | "yesno" | "response";

export interface Question {
  /** after-www's identifier for the entry — the answers map and every
   * analytics property speak it, so it stays verbatim. */
  key: string;
  /** The screen's URL segment: hyphenated, one per entry. */
  slug: string;
  type: QuestionType;
  label?: LabelSegment[];
  /** Swaps in for `label` once the quiz's own `who` answer is
   * `"loved_one"` — never keyed on the server's `planning_for`. */
  labelForLovedOne?: LabelSegment[];
  options?: QuestionOption[];
  /** Multiselect only — the fewest options that must be checked. */
  minRequired?: number;
  content?: ResponseContent;
  /** A response screen the family cannot continue from. */
  isDeadEnd?: boolean;
  /** A response screen's explicit successor, when it isn't the next
   * entry in the list. */
  nextStep?: string;
  /** Answer value → the key to jump to, bypassing the sequential order
   * (and any `showWhen` skip) entirely. */
  conditionalNext?: Record<string, string>;
  /** Show this entry only when every named answer matches — none of the
   * eleven ported questions sets this today, but the engine honors it
   * generically. */
  showWhen?: Record<string, string>;
}

/** What the family has answered so far, by question key. A response
 * screen's key never appears here — advancing past one isn't an answer. */
export type Answers = Record<string, string | string[] | undefined>;

export const PREPLAN_TIMINGS = [
  "right_now",
  "few_weeks",
  "few_months",
  "6_plus_months",
  "not_sure",
] as const;

export type PreplanTiming = (typeof PREPLAN_TIMINGS)[number];

/** The quiz `kind` checkpoint's resolution — mirrors the registry entry
 * after-internal validates a PUT against. */
export type QuizResolution =
  | { conclusion: "completed"; preplan_timing: PreplanTiming }
  | { conclusion: "at_need_imminent" };

export function isPreplanTiming(value: unknown): value is PreplanTiming {
  return (
    typeof value === "string" &&
    (PREPLAN_TIMINGS as readonly string[]).includes(value)
  );
}
